import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  FormControlLabel,
  Switch,
  Grid,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  TextField,
  Checkbox,
} from '@material-ui/core';
import { debounce } from 'lodash';
import {
  SyncAlt as SyncAltIcon,
  GetApp as DownloadIcon,
  Check as CheckIcon,
  Undo as UndoIcon,
} from '@material-ui/icons';
import Page from 'src/components/common/Page';
import {
  useUserGroupInfinity,
  useUserGroupInfinityCount,
  useUpdateMigrationStatus,
} from 'src/hooks/queries/useUserGroups';
import UserGroups from 'src/services/UserGroups';
import LegacyMigration, { ILegacyMigrationPractitioner } from 'src/services/LegacyMigration';
import { IUserGroup, IUserGroupListParams } from 'src/interfaces/IUserGroup';
import { formatLocalDateTime } from 'src/utils/formatDate';
import TablePaginator from 'src/components/common/Table/TablePaginator';
import InputSearch from 'src/components/common/InputSearch';
import { dispatch } from 'src/rematch';

const initialParams: IUserGroupListParams = {
  search: '',
  status: '',
  disabled: false,
  sort: 'created_at',
  sortDirection: 'desc',
};

const MIGRATION_COLUMNS = [
  {
    field: 'id',
    title: 'Medspa ID',
    sortable: true,
    sortField: 'id',
  },
  {
    field: 'name',
    title: 'Medspa Name',
    sortable: true,
    sortField: 'name',
  },
  {
    field: 'ownerFullName',
    title: 'Owner Full Name',
    sortable: false,
  },
  {
    field: 'ownerEmail',
    title: 'Owner Email',
    sortable: false,
  },
  {
    field: 'migrationStatus',
    title: 'Migration Status',
    sortable: true,
    sortField: 'migrated_at',
  },
  {
    field: 'migratedAt',
    title: 'Migrated At',
    sortable: true,
    sortField: 'migrated_at',
  },
  {
    field: 'actions',
    title: 'Actions',
    sortable: false,
  },
];

const LEGACY_COLUMNS = [
  { field: 'id', title: 'Practitioner ID', sortable: false },
  { field: 'name', title: 'Business Name', sortable: false },
  { field: 'ownerFullName', title: 'Owner Full Name', sortable: false },
  { field: 'ownerEmail', title: 'Owner Email', sortable: false },
  { field: 'migrationStatus', title: 'Migration Status', sortable: false },
  { field: 'migratedAt', title: 'Migrated At', sortable: false },
  { field: 'actions', title: 'Actions', sortable: false },
];

// A medspa (user_group) and a solo legacy provider (practitioner) are different records on
// different endpoints, but the confirmation modal asks the same question about both.
interface IMigrationSubject {
  kind: 'medspa' | 'legacy';
  id: number;
  name: string;
  migratedAt: string | null;
}

const renderMigrationStatus = (migratedAt: string | null) =>
  (migratedAt ? (
    <Typography variant="body2" style={{ color: '#4caf50', fontWeight: 'bold' }}>
      Migrated
    </Typography>
  ) : (
    <Typography variant="body2" style={{ color: '#ff9800', fontWeight: 'bold' }}>
      Not Migrated
    </Typography>
  ));

const renderMigratedAt = (migratedAt: string | null) => {
  if (!migratedAt) {
    return '-';
  }
  const date = formatLocalDateTime(migratedAt);
  return `${date.month} ${date.day}, ${date.year} at ${date.time}`;
};

const Migration = () => {
  const [queryParams, setQueryParams] = useState<IUserGroupListParams>(initialParams);
  const [showMigratedOnly, setShowMigratedOnly] = useState(false);
  const [showLegacyProviders, setShowLegacyProviders] = useState(false);
  const [legacyProviders, setLegacyProviders] = useState<ILegacyMigrationPractitioner[]>([]);
  // Merge picker. Opens for an OWNER practitioner before their export starts; the candidate list
  // is fetched UNFILTERED on open rather than read from `legacyProviders`, which is search-scoped
  // — with "Bowers" in the search box Mandy Ward would not be offered at all.
  const [mergeOwner, setMergeOwner] = useState<ILegacyMigrationPractitioner | null>(null);
  const [mergeCandidates, setMergeCandidates] = useState<ILegacyMigrationPractitioner[]>([]);
  const [mergeSelectedIds, setMergeSelectedIds] = useState<number[]>([]);
  const [mergeLoading, setMergeLoading] = useState(false);
  const [legacyLoading, setLegacyLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [downloadMode, setDownloadMode] = useState<'medspa' | 'legacy'>('medspa');
  const [exportJobId, setExportJobId] = useState<number | null>(null);
  const [exportStatus, setExportStatus] = useState<string | null>(null);
  const [exportSubjectName, setExportSubjectName] = useState<string>('');
  // Set immediately before we navigate to the S3 download so our own navigation does not trip
  // the beforeunload guard below. A ref, not state: the assignment must be visible to the
  // handler in the same tick, and a setState would not have committed yet.
  const suppressUnloadWarningRef = React.useRef(false);
  const [externalBusinessId, setExternalBusinessId] = useState('');
  const [legacyUpdating, setLegacyUpdating] = useState(false);
  // Bumped after a legacy migrate/rollback so the provider list refetches its new status.
  const [legacyRefreshKey, setLegacyRefreshKey] = useState(0);
  const [confirmationModal, setConfirmationModal] = useState<{
    open: boolean;
    subject: IMigrationSubject | null;
    action: 'migrate' | 'rollback';
  }>({
    open: false,
    subject: null,
    action: 'migrate',
  });

  const { data: count = 0 } = useUserGroupInfinityCount(queryParams, true);
  const { data, isLoading, isError, hasNextPage, fetchNextPage, isFetchingNextPage } = useUserGroupInfinity(
    queryParams,
    count,
    true
  );
  const { mutateAsync: updateMigrationStatus, isLoading: isUpdating } = useUpdateMigrationStatus();

  const debounceCall = (query: string, params: IUserGroupListParams) => {
    const userGroupParams = { ...params, search: query };
    setQueryParams(userGroupParams);
  };

  const doSearch = useMemo(
    () => debounce((query: string, params: IUserGroupListParams) => debounceCall(query, params), 1000),
    []
  );

  const onInputChange = (event: any) => {
    const newSearchQuery = event.target.value;
    setSearchQuery(newSearchQuery);
    doSearch(newSearchQuery, queryParams);
  };

  const onChangeSortBy = (column: string, direction: string) => {
    setQueryParams({ ...queryParams, sort: column, sortDirection: direction });
  };

  // Poll for export status
  React.useEffect(() => {
    if (!downloadingId || !exportJobId) {
      return;
    }

    // Function to check export status
    const checkExportStatus = async () => {

      try {
        const response = downloadMode === 'legacy'
          ? await LegacyMigration.getExportStatus(downloadingId, exportJobId)
          : await UserGroups.getExportStatus(downloadingId, exportJobId);

        if (response.success && response.data) {
          setExportStatus(response.data.status);

          if (response.data.status === 'completed' && response.data.s3Url) {
            // Download the file from S3.
            // Assigning location.href IS a navigation, so it fires beforeunload; suppress our
            // guard first or the browser prompts "Leave site?" on every successful export (and,
            // if the user picks Stay, cancels the download while the UI reports success).
            suppressUnloadWarningRef.current = true;
            window.location.href = response.data.s3Url;

            // Show success message
            dispatch({
              type: 'snackbar/enqueueSnackBar',
              payload: {
                type: 'success',
                message: 'Export completed successfully! Download started.',
              },
            });

            // Clear state
            setDownloadingId(null);
            setExportJobId(null);
            setExportStatus(null);
            return true; // Signal to stop polling
            // eslint-disable-next-line no-else-return  
          } else if (response.data.status === 'failed') {
            // Display error to user
            dispatch({
              type: 'snackbar/enqueueSnackBar',
              payload: {
                type: 'error',
                message: `Export failed: ${response.data.errorMessage || 'Unknown error'}`,
              },
            });
            setDownloadingId(null);
            setExportJobId(null);
            setExportStatus(null);
            return true; // Signal to stop polling
          }
        }
      } catch (error) {
        // Surface a generic message to the admin; keep the real error in the console for support.
        console.error('Legacy export status poll failed', error);
        dispatch({
          type: 'snackbar/enqueueSnackBar',
          payload: {
            type: 'error',
            message: 'Error checking export status. Please try again.',
          },
        });
        setDownloadingId(null);
        setExportJobId(null);
        setExportStatus(null);
        return true; // Signal to stop polling
      }
      return false; // Continue polling
    };

    // Check immediately. Not awaited: checkExportStatus handles its own errors (snackbar + state
    // reset) and never rejects, so the handler here exists only to make that explicit.
    checkExportStatus().catch(() => undefined);

    // Then poll every 3 seconds
    const pollInterval = setInterval(async () => {
      const shouldStop = await checkExportStatus();
      if (shouldStop) {
        clearInterval(pollInterval);
      }
    }, 3000);

    // Cleanup on unmount
    // eslint-disable-next-line consistent-return
    return () => {
      clearInterval(pollInterval);
    };
  }, [downloadingId, exportJobId, downloadMode]);

  // An export now takes minutes (the photo step alone walks tens of thousands of rows), and the
  // job id lives only in component state -- a refresh orphans a job that keeps running server-side
  // and cannot be reattached to. The modal is advisory; this is what actually intercepts the
  // refresh, using the browser's own confirmation.
  React.useEffect(() => {
    if (!downloadingId || !exportJobId) {
      return undefined;
    }

    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      // Our own download navigation is not the user leaving -- do not warn about it.
      if (suppressUnloadWarningRef.current) {
        return undefined;
      }

      // preventDefault() is the standard way to ask for the leave-page prompt; every browser we
      // support honours it, and the deprecated `returnValue` string is ignored by all of them
      // (they show their own generic wording), so it is not set.
      event.preventDefault();
      return undefined;
    };

    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, [downloadingId, exportJobId]);

  // Fetch solo legacy providers (no user_group) when that mode is on.
  React.useEffect(() => {
    if (!showLegacyProviders) {
      return undefined;
    }
    let active = true;
    setLegacyLoading(true);
    LegacyMigration.getPractitioners(searchQuery)
      .then((res) => {
        if (active && res.success) {
          setLegacyProviders(res.data || []);
        }
      })
      .catch(() => {
        if (active) {
          dispatch({
            type: 'snackbar/enqueueSnackBar',
            payload: { type: 'error', message: 'Failed to load legacy providers.' },
          });
        }
      })
      .finally(() => {
        if (active) {
          setLegacyLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [showLegacyProviders, searchQuery, legacyRefreshKey]);

  const handleDownloadCSV = async (userGroup: IUserGroup) => {
    try {
      suppressUnloadWarningRef.current = false;
      setDownloadMode('medspa');
      setDownloadingId(userGroup.id);
      setExportStatus('pending');
      setExportSubjectName(userGroup.name);

      // Start the export job
      const response = await UserGroups.startExportJob(userGroup.id);

      if (response.success && response.data) {
        setExportJobId(response.data.jobId);
        // Polling will be handled by useEffect
      } else {
        throw new Error('Failed to start export job');
      }
    } catch (error) {
      console.error('Starting the medspa export failed', error);
      dispatch({
        type: 'snackbar/enqueueSnackBar',
        payload: {
          type: 'error',
          message: 'Failed to start export. Please try again.',
        },
      });
      setDownloadingId(null);
      setExportJobId(null);
      setExportStatus(null);
    }
  };

  const practitionerLabel = (p: ILegacyMigrationPractitioner) =>
    p.name || p.ownerFullName || `Practitioner ${p.id}`;

  const openMergePicker = async (practitioner: ILegacyMigrationPractitioner) => {
    setMergeOwner(practitioner);
    setMergeSelectedIds([]);
    setMergeLoading(true);
    try {
      const res = await LegacyMigration.getPractitioners();
      // Already-migrated providers are excluded: their data is in a business of its own.
      setMergeCandidates(
        (res.data || []).filter((p) => p.id !== practitioner.id && !p.migratedAt),
      );
    } catch {
      setMergeCandidates([]);
    } finally {
      setMergeLoading(false);
    }
  };

  const toggleMergeCandidate = (id: number) =>
    setMergeSelectedIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));

  const confirmMergeAndExport = async () => {
    if (!mergeOwner) {
      return;
    }
    const owner = mergeOwner;
    const mergeIds = mergeSelectedIds;
    setMergeOwner(null);
    await handleDownloadPractitioner(owner, mergeIds);
  };

  const handleDownloadPractitioner = async (
    practitioner: ILegacyMigrationPractitioner,
    mergePractitionerIds: number[] = [],
  ) => {
    try {
      suppressUnloadWarningRef.current = false;
      setDownloadMode('legacy');
      setDownloadingId(practitioner.id);
      setExportStatus('pending');
      const mergedNames = mergeCandidates
        .filter((p) => mergePractitionerIds.includes(p.id))
        .map(practitionerLabel);
      setExportSubjectName(
        mergedNames.length
          ? `${practitionerLabel(practitioner)} + ${mergedNames.join(', ')}`
          : practitionerLabel(practitioner),
      );

      const response = await LegacyMigration.startExportJob(practitioner.id, mergePractitionerIds);

      if (response.success && response.data) {
        setExportJobId(response.data.jobId);
        // Polling handled by the shared useEffect (downloadMode === 'legacy').
      } else {
        throw new Error('Failed to start export job');
      }
    } catch (error) {
      console.error('Starting the legacy provider export failed', error);
      dispatch({
        type: 'snackbar/enqueueSnackBar',
        payload: {
          type: 'error',
          message: 'Failed to start export. Please try again.',
        },
      });
      setDownloadingId(null);
      setExportJobId(null);
      setExportStatus(null);
    }
  };

  const handleToggleMigration = (subject: IMigrationSubject) => {
    setConfirmationModal({
      open: true,
      subject,
      action: subject.migratedAt ? 'rollback' : 'migrate',
    });
  };

  const handleCloseModal = () => {
    setConfirmationModal({
      open: false,
      subject: null,
      action: 'migrate',
    });
    setExternalBusinessId('');
  };

  const handleConfirmAction = async () => {
    const { subject, action } = confirmationModal;
    if (!subject) {
      return;
    }

    const migrated = action === 'migrate';

    if (subject.kind === 'legacy') {
      // No react-query cache behind the legacy list, so the snackbar and the refetch are ours.
      try {
        setLegacyUpdating(true);
        await LegacyMigration.updateMigrationStatus(
          subject.id,
          migrated,
          migrated ? externalBusinessId.trim() : null
        );
        setLegacyRefreshKey((key) => key + 1);
        dispatch({
          type: 'snackbar/enqueueSnackBar',
          payload: { type: 'success', message: 'Migration status updated successfully' },
        });
      } catch (error) {
        console.error('Updating the legacy migration status failed', error);
        dispatch({
          type: 'snackbar/enqueueSnackBar',
          payload: { type: 'error', message: 'Failed to update migration status' },
        });
      } finally {
        setLegacyUpdating(false);
        handleCloseModal();
      }
      return;
    }

    try {
      await updateMigrationStatus({
        userGroupId: subject.id,
        migrated,
        externalBusinessId: migrated ? externalBusinessId : null,
      });
    } catch (error) {
      // Error is handled by the mutation hook with snackbar
    } finally {
      handleCloseModal();
    }
  };

  const isPending = isUpdating || legacyUpdating;
  // ellamd-api rejects a legacy migrate without one (422), so block the button rather than
  // surface a server error the operator can only fix by reopening the modal.
  const missingExternalBusinessId =
    confirmationModal.action === 'migrate'
    && confirmationModal.subject?.kind === 'legacy'
    && !externalBusinessId.trim();

  const allUserGroups = useMemo(() => {
    if (!data?.pages) {
      return [];
    }
    return data.pages.flat();
  }, [data]);

  const filteredUserGroups = useMemo(() => {
    if (!showMigratedOnly) {
      return allUserGroups;
    }
    return allUserGroups.filter((group: IUserGroup) => group.migratedAt);
  }, [allUserGroups, showMigratedOnly]);

  const userGroupRows = useMemo(() =>
    filteredUserGroups.map((userGroup: IUserGroup) => ({
      id: userGroup.id,
      name: userGroup.name,
      ownerFullName: userGroup.owner?.fullName || '-',
      ownerEmail: userGroup.owner?.email || '-',
      migrationStatus: renderMigrationStatus(userGroup.migratedAt),
      migratedAt: renderMigratedAt(userGroup.migratedAt),
      actions: (
        <Box display="flex" gap={1}>
          <IconButton
            size="small"
            title={
              downloadingId === userGroup.id
                ? `Export ${exportStatus || 'pending'}...`
                : 'Download export files'
            }
            onClick={() => handleDownloadCSV(userGroup)}
            disabled={downloadingId === userGroup.id}
            style={{ color: downloadingId === userGroup.id ? '#ccc' : '#1976d2' }}
          >
            {downloadingId === userGroup.id ? (
              <CircularProgress size={20} />
            ) : (
              <DownloadIcon />
            )}
          </IconButton>
          <IconButton
            size="small"
            title={userGroup.migratedAt ? 'Rollback Migration' : 'Mark as Migrated'}
            onClick={() => handleToggleMigration({
              kind: 'medspa',
              id: userGroup.id,
              name: userGroup.name,
              migratedAt: userGroup.migratedAt || null,
            })}
            style={{ color: userGroup.migratedAt ? '#f44336' : '#4caf50' }}
          >
            {userGroup.migratedAt ? <UndoIcon /> : <CheckIcon />}
          </IconButton>
        </Box>
      ),
    })), [filteredUserGroups, downloadingId, exportStatus]
  );

  const legacyRows = useMemo(() =>
    legacyProviders.map((practitioner: ILegacyMigrationPractitioner) => ({
      id: practitioner.id,
      name: practitioner.name || '-',
      ownerFullName: practitioner.ownerFullName || '-',
      ownerEmail: practitioner.ownerEmail || '-',
      migrationStatus: renderMigrationStatus(practitioner.migratedAt),
      migratedAt: renderMigratedAt(practitioner.migratedAt),
      actions: (
        <Box display="flex" gap={1}>
          <IconButton
            size="small"
            title={
              downloadingId === practitioner.id
                ? `Export ${exportStatus || 'pending'}...`
                : 'Download export files'
            }
            onClick={() => openMergePicker(practitioner)}
            disabled={downloadingId === practitioner.id}
            style={{ color: downloadingId === practitioner.id ? '#ccc' : '#1976d2' }}
          >
            {downloadingId === practitioner.id ? <CircularProgress size={20} /> : <DownloadIcon />}
          </IconButton>
          <IconButton
            size="small"
            title={practitioner.migratedAt ? 'Rollback Migration' : 'Mark as Migrated'}
            onClick={() => handleToggleMigration({
              kind: 'legacy',
              id: practitioner.id,
              name: practitioner.name || practitioner.ownerFullName || `Practitioner ${practitioner.id}`,
              migratedAt: practitioner.migratedAt,
            })}
            style={{ color: practitioner.migratedAt ? '#f44336' : '#4caf50' }}
          >
            {practitioner.migratedAt ? <UndoIcon /> : <CheckIcon />}
          </IconButton>
        </Box>
      ),
    })), [legacyProviders, downloadingId, exportStatus]
  );

  const renderEmptyState = () => (
    <Paper elevation={2} style={{ padding: '40px', textAlign: 'center' }}>
      <SyncAltIcon style={{ fontSize: '60px', color: '#666', marginBottom: '20px' }} />
      <Typography variant="h5" gutterBottom>
        {showMigratedOnly ? 'No Migrated Medspas Found' : 'No Medspas Found'}
      </Typography>
      <Typography variant="body1" color="textSecondary">
        {showMigratedOnly
          ? 'No medspas have been migrated yet. Toggle off the filter to see all medspas.'
          : 'No medspas found. Check your filters or try refreshing the page.'
        }
      </Typography>
    </Paper>
  );

  const renderTable = () => (
    <TablePaginator
      isLoading={isLoading}
      isFetching={isFetchingNextPage}
      isError={isError}
      data={userGroupRows}
      columns={MIGRATION_COLUMNS}
      onChangeSortBy={onChangeSortBy}
      total={count}
      fetchNextPage={hasNextPage ? fetchNextPage : undefined}
      backgroundAlternated={false}
    />
  );

  const renderLoading = () => (
    <Paper elevation={2} style={{ padding: '40px', textAlign: 'center' }}>
      <CircularProgress style={{ marginBottom: '20px' }} />
      <Typography variant="body1">Loading medspas...</Typography>
    </Paper>
  );

  const renderLegacyTable = () => (
    <TablePaginator
      isLoading={legacyLoading}
      isFetching={false}
      isError={false}
      data={legacyRows}
      columns={LEGACY_COLUMNS}
      onChangeSortBy={() => {}}
      total={legacyRows.length}
      backgroundAlternated={false}
    />
  );

  const renderLegacyEmpty = () => (
    <Paper elevation={2} style={{ padding: '40px', textAlign: 'center' }}>
      <SyncAltIcon style={{ fontSize: '60px', color: '#666', marginBottom: '20px' }} />
      <Typography variant="h5" gutterBottom>No Legacy Providers Found</Typography>
      <Typography variant="body1" color="textSecondary">
        No solo legacy providers match your search.
      </Typography>
    </Paper>
  );

  const renderError = () => (
    <Paper elevation={2} style={{ padding: '40px', textAlign: 'center' }}>
      <Typography variant="h6" color="error" gutterBottom>
        Error Loading Data
      </Typography>
      <Typography variant="body1" color="textSecondary">
        Unable to load medspas. Please refresh the page or try again later.
      </Typography>
    </Paper>
  );

  return (
    <Page title="Migration">
      <Box style={{ width: '100%', padding: '20px' }}>
        <Grid container spacing={3} style={{ marginBottom: '30px' }}>
          <Grid item xs={12} sm={4}>
            <InputSearch
              placeholder="Search medspas..."
              onChangeValue={onInputChange}
              value={searchQuery}
              isLoading={isLoading}
            />
          </Grid>
          <Grid item xs={12} sm={4} style={{ textAlign: 'center' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={showLegacyProviders}
                  onChange={(e) => setShowLegacyProviders(e.target.checked)}
                  color="primary"
                />
              }
              label="Legacy Providers (solo)"
            />
          </Grid>
          <Grid item xs={12} sm={4} style={{ textAlign: 'right' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={showMigratedOnly}
                  onChange={(e) => setShowMigratedOnly(e.target.checked)}
                  color="primary"
                  disabled={showLegacyProviders}
                />
              }
              label="Show Migrated Only"
            />
          </Grid>
        </Grid>

        {showLegacyProviders ? (
          <>
            {legacyLoading && renderLoading()}
            {!legacyLoading && legacyRows.length === 0 && renderLegacyEmpty()}
            {!legacyLoading && legacyRows.length > 0 && renderLegacyTable()}
          </>
        ) : (
          <>
            {isLoading && renderLoading()}
            {isError && renderError()}
            {!isLoading && !isError && userGroupRows.length === 0 && renderEmptyState()}
            {!isLoading && !isError && userGroupRows.length > 0 && renderTable()}
          </>
        )}

        {/* Export-in-progress. Not dismissible: the job id lives only in component state, so losing
            this view orphans a job that keeps running server-side. Deliberately offers no Cancel --
            "cancelling" only marks the DB row failed while the Sidekiq thread runs to completion and
            then flips it back to completed, so a Cancel button would tell the user something false. */}
        <Dialog
          open={Boolean(downloadingId && exportJobId)}
          disableBackdropClick
          disableEscapeKeyDown
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Generating export</DialogTitle>
          <DialogContent>
            <Box display="flex" alignItems="center" mb={2}>
              <CircularProgress size={24} />
              <Box ml={2}>
                <Typography variant="body1">
                  <strong>{exportSubjectName}</strong>
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {exportStatus === 'processing' ? 'Building the CSV bundle...' : 'Queued...'}
                </Typography>
              </Box>
            </Box>
            <DialogContentText>
              This can take several minutes for providers with a lot of history &mdash; the export
              walks every patient, visit and photo.
              <br />
              <br />
              <strong>Please do not refresh or leave this page.</strong> The download starts
              automatically as soon as the file is ready.
            </DialogContentText>
          </DialogContent>
        </Dialog>

        <Dialog open={confirmationModal.open} onClose={handleCloseModal} maxWidth="sm" fullWidth>
          <DialogTitle>
            {confirmationModal.action === 'migrate' ? 'Mark as Migrated' : 'Rollback Migration'}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              {confirmationModal.action === 'migrate' ? (
                <>
                  Are you sure you want to mark <strong>{confirmationModal.subject?.name}</strong> as migrated?
                  <br />
                  <br />
                  This operation will change the medspa to &quot;view only mode&quot;, the users will not be able to
                  edit, delete or add new information.
                  {confirmationModal.subject?.kind === 'legacy' && (
                    <>
                      <br />
                      <br />
                      It also sends this provider&apos;s next login to the migration choice screen and
                      redirects their patients to the new patient portal. Only do this after the FINAL
                      re-migration.
                    </>
                  )}
                </>
              ) : (
                <>
                  Are you sure you want to rollback the migration for{' '}
                  <strong>{confirmationModal.subject?.name}</strong>?
                  <br />
                  <br />
                  This will restore full editing capabilities for the medspa users.
                </>
              )}
            </DialogContentText>
            {confirmationModal.action === 'migrate' && (
              <TextField
                fullWidth
                required={confirmationModal.subject?.kind === 'legacy'}
                label="External Business ID"
                value={externalBusinessId}
                onChange={(e) => setExternalBusinessId(e.target.value)}
                variant="outlined"
                margin="normal"
                placeholder="Enter external business ID"
                helperText={
                  confirmationModal.subject?.kind === 'legacy'
                    ? 'The new EHR business id or hashid (e.g. bK7umB). Required — it is what the '
                      + 'patient portal redirect targets.'
                    : undefined
                }
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseModal} color="default">
              Cancel
            </Button>
            <Button
              onClick={handleConfirmAction}
              color={confirmationModal.action === 'migrate' ? 'primary' : 'secondary'}
              variant="contained"
              disabled={isPending || missingExternalBusinessId}
            >
              {(() => {
                if (isPending) {
                  return 'Updating...';
                }
                return confirmationModal.action === 'migrate' ? 'Mark as Migrated' : 'Rollback Migration';
              })()}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Merge picker: which other legacy practices fold INTO this owner's export. Shown for
            every legacy export so a solo provider confirms with nothing ticked; a merge is a
            deliberate choice, never a default. Candidates come from an unfiltered fetch — see
            openMergePicker. */}
        <Dialog open={mergeOwner !== null} onClose={() => setMergeOwner(null)} maxWidth="sm" fullWidth>
          <DialogTitle>
            Export {mergeOwner ? practitionerLabel(mergeOwner) : ''}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              Optionally merge other legacy practices into this export. Their patients, visits,
              inventory and services are folded into {mergeOwner ? practitionerLabel(mergeOwner) : 'this provider'}
              &apos;s business. Where both price the same service, the owner&apos;s price is kept.
            </DialogContentText>
            {mergeLoading && (
              <Box display="flex" justifyContent="center" py={2}>
                <CircularProgress size={24} />
              </Box>
            )}
            {!mergeLoading && mergeCandidates.length === 0 && (
              <Typography variant="body2" color="textSecondary">
                No other un-migrated legacy practices to merge.
              </Typography>
            )}
            {!mergeLoading && mergeCandidates.length > 0 && (
              <Box display="flex" flexDirection="column" data-testid="merge-candidates">
                {mergeCandidates.map((candidate) => (
                  <FormControlLabel
                    key={candidate.id}
                    control={
                      <Checkbox
                        checked={mergeSelectedIds.includes(candidate.id)}
                        onChange={() => toggleMergeCandidate(candidate.id)}
                        color="primary"
                        inputProps={{ 'aria-label': `Merge ${practitionerLabel(candidate)}` }}
                      />
                    }
                    label={[practitionerLabel(candidate), candidate.ownerEmail].filter(Boolean).join(' — ')}
                  />
                ))}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setMergeOwner(null)}>Cancel</Button>
            <Button onClick={confirmMergeAndExport} color="primary" variant="contained" disabled={mergeLoading}>
              {mergeSelectedIds.length
                ? `Export with ${mergeSelectedIds.length} merged`
                : 'Export'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Page>
  );
};

export default Migration;